import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

interface BookingOptionsProps {
  step: number; // 3, 4, or 5
  selectedDate?: string;
  timeSlots: string[];
  selectedTime: string;
  selectedDuration: number;
  selectedGuests: number;
  onSelectTime: (time: string) => void;
  onSelectDuration: (duration: number) => void;
  onSelectGuests: (guests: number) => void;
  onNextStep: () => void;
  onBack: () => void;
}

export const BookingOptions: React.FC<BookingOptionsProps> = ({
  step,
  selectedDate,
  timeSlots,
  selectedTime,
  selectedDuration,
  selectedGuests,
  onSelectTime,
  onSelectDuration,
  onSelectGuests,
  onNextStep,
  onBack,
}) => {
  const { t } = useTranslation("common");
  const [customDuration, setCustomDuration] = useState<number>(
    selectedDuration || 120
  );

  const isTimePast = (timeSlot: string): boolean => {
    if (!selectedDate) return false;

    const now = new Date();
    const bookingDate = new Date(selectedDate);

    const isToday =
      bookingDate.getFullYear() === now.getFullYear() &&
      bookingDate.getMonth() === now.getMonth() &&
      bookingDate.getDate() === now.getDate();

    if (!isToday) return false;

    const [hours, minutes] = timeSlot.split(":").map(Number);
    const slotTime = new Date();
    slotTime.setHours(hours, minutes, 0, 0);

    return slotTime <= now;
  };

  // Helper: Render Header with Back Button
  const renderHeaderWithBack = (title: string) => (
    <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 pb-3 mb-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 font-medium transition-colors cursor-pointer"
      >
        <ArrowLeft size={15} />
        <span>{t("booking.back", "Wstecz")}</span>
      </button>
      <h3 className="text-xs uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400 font-serif font-bold">
        {title}
      </h3>
      <div className="w-10" />
    </div>
  );

  // Step 3: Duration
  if (step === 3) {
    const durationOptions = [
      {
        mins: 60,
        label: t("booking.durationQuick", "Szybki posiłek"),
        hoursLabel: t("booking.hoursFormat", "{{hours}} godz.", { hours: 1 }),
      },
      {
        mins: 120,
        label: t("booking.durationLunch", "Obiad / Przekąska"),
        hoursLabel: t("booking.hoursFormat", "{{hours}} godz.", { hours: 2 }),
      },
      {
        mins: 180,
        label: t("booking.durationDinner", "Długa Kolacja"),
        hoursLabel: t("booking.hoursFormat", "{{hours}} godz.", { hours: 3 }),
      },
    ];

    return (
      <div className="p-5 sm:p-6 space-y-4">
        {renderHeaderWithBack(
          t("booking.selectDuration", "Czas trwania wizyty")
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {durationOptions.map((opt) => (
            <button
              key={opt.mins}
              type="button"
              onClick={() => {
                setCustomDuration(opt.mins);
                onSelectDuration(opt.mins);
                onNextStep();
              }}
              className={`rounded-2xl border transition-all duration-300 text-left p-4 cursor-pointer flex flex-col justify-between ${
                selectedDuration === opt.mins
                  ? "bg-amber-600/10 border-amber-500 ring-2 ring-amber-500/30 shadow-md"
                  : "bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800 hover:border-amber-500/50"
              }`}
            >
              <div>
                <span className="text-xl font-serif font-bold text-stone-900 dark:text-white">
                  {opt.mins} {t("booking.minUnit", "min")}
                </span>
              </div>
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-2">
                {opt.label}
              </span>
            </button>
          ))}
        </div>

        {/* En: Custom Duration Input */}
        <div className="bg-stone-50 dark:bg-stone-900/60 p-4 rounded-2xl border border-stone-200 dark:border-stone-800 space-y-3">
          <label className="block text-xs font-medium text-stone-700 dark:text-stone-300">
            {t(
              "booking.customDurationLabel",
              "Lub wpisz własny czas (w minutach):"
            )}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={30}
              max={480}
              step={15}
              value={customDuration}
              onChange={(e) => {
                const val = Number(e.target.value);
                setCustomDuration(val);
                onSelectDuration(val); // En: Update the selected duration in the parent component
              }}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
            <span className="text-xs text-stone-500 whitespace-nowrap font-medium">
              {t("booking.minUnit", "min")} (~{(customDuration / 60).toFixed(1)}{" "}
              godz.)
            </span>
          </div>

          <button
            type="button"
            onClick={onNextStep}
            className="w-full mt-2 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-serif font-semibold text-xs rounded-xl transition shadow-md cursor-pointer"
          >
            {t("booking.next", "Dalej")} &rarr;
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Select Time Slot
  if (step === 4) {
    const lunchSlots = timeSlots.filter(
      (time) => time >= "12:00" && time < "16:00"
    );
    const afternoonSlots = timeSlots.filter(
      (time) => time >= "16:00" && time < "19:00"
    );
    const eveningSlots = timeSlots.filter(
      (time) => time >= "19:00" || time < "12:00"
    );

    const renderSlotGroup = (title: string, icon: string, slots: string[]) => {
      if (slots.length === 0) return null;

      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-serif text-xs font-bold uppercase tracking-wider border-b border-stone-200 dark:border-stone-800 pb-1.5">
            <span>{icon}</span>
            <span className="font-bold">{title}</span>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {slots.map((time) => {
              const disabled = isTimePast(time);
              const isSelected = selectedTime === time;

              return (
                <button
                  key={time}
                  disabled={disabled}
                  onClick={() => !disabled && onSelectTime(time)}
                  className={`py-3 rounded-xl text-xs font-serif font-bold tracking-wider transition-all duration-200 border cursor-pointer ${
                    disabled
                      ? "opacity-30 bg-stone-100 dark:bg-stone-900/50 text-stone-400 border-stone-200 dark:border-stone-800 cursor-not-allowed line-through"
                      : isSelected
                      ? "bg-amber-600 text-white border-amber-500 shadow-lg shadow-amber-950/40 scale-[1.02]"
                      : "bg-white dark:bg-stone-900 text-stone-800 dark:text-stone-200 border-stone-200 dark:border-stone-800 hover:border-amber-500 hover:bg-stone-50 dark:hover:bg-stone-800/80"
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="p-5 sm:p-6 space-y-6 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar">
        {renderHeaderWithBack(t("booking.selectTime", "Wybierz godzinę"))}

        {renderSlotGroup(t("booking.lunch", "Obiad / Lunch"), "☀️", lunchSlots)}
        {renderSlotGroup(
          t("booking.afternoon", "Popołudnie"),
          "🌤️",
          afternoonSlots
        )}
        {renderSlotGroup(
          t("booking.evening", "Wieczór / Kolacja"),
          "🌙",
          eveningSlots
        )}
      </div>
    );
  }

  // Step 5: Guest Count
  if (step === 5) {
    return (
      <div className="p-5 sm:p-6 space-y-6">
        {renderHeaderWithBack(t("booking.selectGuests", "Liczba osób"))}

        <div className="grid grid-cols-2 gap-4 mb-2">
          <div className="relative overflow-hidden h-28 rounded-2xl border border-stone-800 p-3 group">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
              style={{
                backgroundImage: "url('/images/booking/guests-couple.jpg')",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end h-full">
              <span className="text-sm font-serif font-bold text-white">
                1 - 2 {t("booking.guestsCountUnit", "Osoby")}
              </span>
              <span className="text-[10px] text-amber-400">
                {t("booking.guestCategorySmall", "Kameralne spotkanie")}
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden h-28 rounded-2xl border border-stone-800 p-3 group">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
              style={{
                backgroundImage: "url('/images/booking/guests-company.jpg')",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end h-full">
              <span className="text-sm font-serif font-bold text-white">
                3 - 8 {t("booking.guestsCountUnitPlural", "Osób")}
              </span>
              <span className="text-[10px] text-amber-400">
                {t("booking.guestCategoryLarge", "Większa grupa / Rodzina")}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2.5">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
            <button
              key={num}
              onClick={() => onSelectGuests(num)}
              className={`py-3.5 rounded-xl border text-center font-serif transition-all duration-200 cursor-pointer flex flex-col items-center justify-center ${
                selectedGuests === num
                  ? "bg-amber-600 text-white border-amber-500 shadow-lg shadow-amber-950/40 scale-105"
                  : "bg-white dark:bg-stone-900 text-stone-800 dark:text-stone-200 border-stone-200 dark:border-stone-800 hover:border-amber-500 hover:bg-stone-100 dark:hover:bg-stone-800"
              }`}
            >
              <span className="text-lg font-bold">{num}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
};
